import {
    ProcessCellForExportParams,
    ColDef,
    ValueFormatterParams,
    ICellRendererParams,
    ValueGetterParams,
    CellRendererSelectorFunc,
    KeyCreatorParams,
} from 'ag-grid-community';
import { capitalize } from 'helpers/helpers';
import { t } from 'i18next';
import { SPLIT_BY } from 'routes/BudgetModule/config/constants';
import {
    AGENT_FIRST_NAME,
    AGENT_LAST_NAME,
    AGENT_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import { SemiTreatmentHypothesis } from 'routes/HumanResourcesModule/Simulation/helpers/hooks/semiTreatment';
import {
    ENVELOPE_SOFTWARE_ID_MANTY_STATUS,
    ENVELOPE_SOFTWARE_ID_PAY_COMPONENT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/helpers/constants';
import { TEXT_FILTER } from './defaultColDefs';
import { SimulationAgentData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import {
    dashDateStringToDate,
    dashDateStringToMonth,
    dateStringToDate,
} from 'routes/HumanResourcesModule/business/helpers';
import {
    DashDateString,
    DateString,
} from 'routes/HumanResourcesModule/business/types';

export const AGENT_FIELD = 'agentField';

/**
 * Checks if the value indicates an "envelope" agent.
 * Sometimes the amounts of the envelope are allocated to an agent named "envelope."
 * In such cases, a specific cell renderer is used.
 *
 * The value can either be a string or an object containing an AGENT_SOFTWARE_ID field.
 * - When it's a string, it's usually directly coming from a data cell.
 * - When it's an object, it's generally because the data cell is complex and has been
 *   processed by a `valueGetter` function in the column definition.
 *
 * @param {string | { [AGENT_SOFTWARE_ID]?: string } | undefined} value - The value to check. Can either be a string or an object with AGENT_SOFTWARE_ID.
 * @returns {boolean} - True if the value indicates an "envelope" agent, false otherwise.
 */
const isEnvelopeValue = (value: unknown): boolean => {
    const envelopeSoftwareIds = [
        ENVELOPE_SOFTWARE_ID_MANTY_STATUS,
        ENVELOPE_SOFTWARE_ID_PAY_COMPONENT,
    ];

    if (typeof value === 'string') {
        return envelopeSoftwareIds.some((envelopeSoftwareId) =>
            value.includes(envelopeSoftwareId),
        );
    }

    if (value && typeof value === 'object' && AGENT_SOFTWARE_ID in value) {
        const agentSoftwareId = value[AGENT_SOFTWARE_ID as keyof typeof value];
        if (typeof agentSoftwareId === 'string') {
            return envelopeSoftwareIds.some((envelopeSoftwareId) =>
                (agentSoftwareId as string).includes(envelopeSoftwareId),
            );
        }
    }

    return false;
};

const getAgentSoftwareIdFromValue = (value: unknown): string | null => {
    if (typeof value === 'string' && value.includes(SPLIT_BY)) {
        return value.split(SPLIT_BY)[1];
    }
    if (typeof value === 'object' && value && AGENT_SOFTWARE_ID in value) {
        const agentSoftwareId = value[AGENT_SOFTWARE_ID as keyof typeof value];
        if (typeof agentSoftwareId === 'string') {
            return agentSoftwareId;
        }
    }
    return null;
};

const getSemiTreatmentHypothesisForAgent = (
    value: unknown,
    semiTreatmentHypotheses: SemiTreatmentHypothesis[],
): SemiTreatmentHypothesis | undefined => {
    const agentSoftwareId = getAgentSoftwareIdFromValue(value);

    if (agentSoftwareId) {
        const hypothesis = semiTreatmentHypotheses.find(
            (h) => h.agentSoftwareId === agentSoftwareId,
        );
        return hypothesis;
    }
    return undefined;
};
export const isSimulatedAmountData = (
    data: SimulationAgentData | undefined,
): boolean => {
    if (!data) return true;

    const simulatedFields = Object.keys(data)
        .filter((key) => key.startsWith('simulated'))
        .map((key) => data[key])
        .filter((value) => typeof value === 'number') as number[];

    const sum = simulatedFields.reduce((acc, value) => acc + value, 0);
    return sum !== 0;
};

const hasPastHistoricAmountData = (
    data: SimulationAgentData | undefined,
    simulationInitDate?: DashDateString,
): boolean => {
    if (!data || !simulationInitDate) return true;

    return Object.entries(data)
        .filter(
            ([k, v]) =>
                k.startsWith('historic') &&
                typeof v === 'number' &&
                k.includes(SPLIT_BY) &&
                dateStringToDate(k.split(SPLIT_BY)[1] as DateString) <
                    dashDateStringToDate(simulationInitDate),
        )
        .some(([_, v]: [string, number]) => v !== 0);
};

export const agentCellRendererSelectorGetter =
    (
        semiTreatmentHypotheses: SemiTreatmentHypothesis[] = [],
        displayNewEntries: boolean = false,
        simulationInitDate?: DashDateString,
    ) =>
    (params: ICellRendererParams) => {
        if (isEnvelopeValue(params?.value)) {
            return { component: 'envelopeAgentCellRenderer' };
        }
        const hypothesis = getSemiTreatmentHypothesisForAgent(
            params?.value,
            semiTreatmentHypotheses,
        );

        const isAgentHasSimulatedAmounts = isSimulatedAmountData(params?.data);
        const doesAgentHavePastHistoricAmountData = hasPastHistoricAmountData(
            params?.data,
            simulationInitDate,
        );
        if (hypothesis !== undefined) {
            return {
                component: 'agentNameWithTooltipCellRenderer',
                params: {
                    agentAttribute: params.valueFormatted?.toUpperCase() ?? '',
                    tooltipText: t(
                        'humanResources.simulation.agents.semiTreatmentAgent',
                        {
                            startDate: dashDateStringToMonth(
                                hypothesis.startDate,
                                true,
                            ).toLowerCase(),
                        },
                    ),
                },
            };
        } else if (
            !isAgentHasSimulatedAmounts &&
            !doesAgentHavePastHistoricAmountData &&
            displayNewEntries
        ) {
            return {
                component: 'agentNameWithTooltipCellRenderer',
                params: {
                    agentAttribute: params.valueFormatted?.toUpperCase() ?? '',
                    tooltipText: t(
                        'humanResources.simulation.agents.newAgentInOrgChartHr',
                    ),
                },
            };
        }
        return { component: undefined };
    };

export function firstNameLastNameFormatter({
    firstName,
    lastName,
}: {
    firstName?: string;
    lastName?: string;
}): string {
    if (!firstName && !lastName) {
        return '';
    }

    let formattedName = '';

    if (lastName) {
        formattedName += lastName.toUpperCase();
    }

    if (firstName) {
        if (formattedName.length > 0) {
            formattedName += ' ';
        }
        formattedName += capitalize(firstName);
    }

    return formattedName;
}

export const agentValueFormatter = (
    params: ProcessCellForExportParams | ValueFormatterParams,
) =>
    params?.node?.group && typeof params?.value === 'string'
        ? params.value.split('..')[0]
        : firstNameLastNameFormatter({
              firstName: params?.value?.[AGENT_FIRST_NAME],
              lastName: params?.value?.[AGENT_LAST_NAME],
          });

export const agentFilterValueGetter = (params: ValueGetterParams) =>
    firstNameLastNameFormatter({
        firstName: params.data?.[AGENT_FIRST_NAME],
        lastName: params.data?.[AGENT_LAST_NAME],
    });

export const getGroupedAgentColumn = (
    rowGroup: boolean,
    agentCellRendererSelector: CellRendererSelectorFunc = agentCellRendererSelectorGetter(),
): ColDef => ({
    ...TEXT_FILTER,
    headerName: t('humanResources.simulation.agentLastName'),
    field: AGENT_FIELD,
    rowGroup,
    enableRowGroup: true,
    hide: rowGroup,
    valueGetter: (params: ValueGetterParams) => ({
        [AGENT_FIRST_NAME]: params?.data?.[AGENT_FIRST_NAME],
        [AGENT_LAST_NAME]: params?.data?.[AGENT_LAST_NAME],
        [AGENT_SOFTWARE_ID]: params?.data?.[AGENT_SOFTWARE_ID],
    }),
    // The key creator is used to differentiate rows on grouping
    // We need the software id and the last name because the value formatter
    // won't have any data when the row is grouped
    keyCreator: (params: KeyCreatorParams) =>
        `${firstNameLastNameFormatter({
            firstName: params.value[AGENT_FIRST_NAME],
            lastName: params.value[AGENT_LAST_NAME],
        })}..${params.value[AGENT_SOFTWARE_ID]}`,
    valueFormatter: agentValueFormatter,
    filterValueGetter: agentFilterValueGetter,
    cellRendererSelector: agentCellRendererSelector,
    comparator: (
        valueA: string | { [AGENT_LAST_NAME]?: string },
        valueB: string | { [AGENT_LAST_NAME]?: string },
    ) => {
        const valA =
            typeof valueA === 'string' ? valueA : valueA[AGENT_LAST_NAME] || '';
        const valB =
            typeof valueB === 'string' ? valueB : valueB[AGENT_LAST_NAME] || '';

        let result = 0;
        if (valA > valB) {
            result = 1;
        } else if (valA < valB) {
            result = -1;
        }
        return result;
    },
});

export const getAgentColumn = () => ({
    ...getGroupedAgentColumn(false),
});
